/**
 * Pure mappers + partitioners that translate the seasons API response
 * (`SeasonApiRecord[]` returned by `GET /game-pass-admin/v2/admin/seasons`)
 * into the UI model (`SeasonData`) that `SeasonCard` / `SeasonsList`
 * already consume.
 *
 * Centralising every API → UI translation here keeps the page
 * component focused on layout and keeps the mapper testable in
 * isolation — when the backend renames a field or starts shipping a
 * new status enum value, only this module needs to change. Same
 * convention as `src/queries/affiliates/affiliateMappers.ts`.
 */

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { AppStrings } from "../../utils/constants/AppStrings";
import {
  SEASON_API_STATUS,
  SEASON_LIFECYCLE_BUCKET,
  SEASONS_FILTER_ID,
} from "../../types/gamePass";
import type {
  SeasonApiRecord,
  SeasonApiStatus,
  SeasonCountdown,
  SeasonData,
  SeasonLifecycleBucket,
  SeasonMetric,
  SeasonsFilterId,
  SeasonStatus,
  SeasonTrailingKind,
} from "../../types/gamePass";

dayjs.extend(utc);

/** Re-export the canonical lifecycle bucket type so consumers that
 *  already import it from this module keep compiling untouched. The
 *  enum lives in `src/types/gamePass.ts` alongside the BE status enum
 *  so every domain enum has a single source of truth. */
export type { SeasonLifecycleBucket } from "../../types/gamePass";

/** Output shape produced by `partitionSeasonsByStatus`. The active
 *  bucket is a single-or-null slot because the UI only ever surfaces
 *  one Active Season card; the rest are arrays (multiple Scheduled /
 *  Drafts / Completed / Canceled cards co-exist in their sections). */
export type PartitionedSeasons = {
  active: SeasonData | null;
  scheduled: SeasonData[];
  drafts: SeasonData[];
  completed: SeasonData[];
  canceled: SeasonData[];
};

/** Map a UI filter-tab id onto the `?status=` query param the seasons
 *  API accepts. Single-status tabs (`scheduled`, `drafts`, `completed`,
 *  `canceled`) translate 1:1 onto the matching BE status so React
 *  Query refetches with the new query key when the user switches
 *  tabs. Multi-status tabs (`timeline` → Active + Scheduled,
 *  `all` → every lifecycle) return `undefined` so the page fetches
 *  the unfiltered set and partitions client-side — there's no
 *  single-status filter that captures their union.
 *
 *  Centralising this mapping here (instead of inlining a switch in
 *  the component) keeps the API contract auditable in one place and
 *  makes it trivial to extend when BE introduces a new filter
 *  shorthand. */
export const filterIdToApiStatus = (
  filterId: SeasonsFilterId,
): SeasonApiStatus | undefined => {
  switch (filterId) {
    case SEASONS_FILTER_ID.SCHEDULED:
      return SEASON_API_STATUS.SCHEDULED;
    case SEASONS_FILTER_ID.DRAFTS:
      return SEASON_API_STATUS.DRAFT;
    case SEASONS_FILTER_ID.COMPLETED:
      return SEASON_API_STATUS.COMPLETED;
    case SEASONS_FILTER_ID.CANCELED:
      return SEASON_API_STATUS.CANCELLED;
    case SEASONS_FILTER_ID.TIMELINE:
    case SEASONS_FILTER_ID.ALL:
      return undefined;
    default: {
      const _exhaustive: never = filterId;
      return undefined;
    }
  }
};

/** Plural counterpart of `filterIdToApiStatus`. Maps a UI filter onto
 *  the *set* of BE statuses that need to be fetched to populate the
 *  panel.
 *
 *  Most tabs map onto a single status (and the array has one entry),
 *  but the Timeline tab needs active + scheduled-side records to
 *  populate its two columns — so the caller fires one `useQueries`
 *  bundle that issues parallel `?status=` requests instead of one
 *  over-fetched unfiltered call. Returning
 *  `undefined` (as opposed to an empty array) signals "no `status=`
 *  filter at all — give me the whole list", which is what the "All"
 *  tab wants since it surfaces every lifecycle bucket and there's no
 *  short list of statuses that captures the union exhaustively
 *  (`locked` / `grace` / `cancelled` etc. would all need to be added
 *  explicitly).
 *
 *  Timeline deliberately includes `locked` so the Scheduled column shows
 *  "Up Next" seasons as well as plain scheduled ones. */
export const filterIdToApiStatuses = (
  filterId: SeasonsFilterId,
): ReadonlyArray<SeasonApiStatus> | undefined => {
  switch (filterId) {
    case SEASONS_FILTER_ID.TIMELINE:
      return [
        SEASON_API_STATUS.ACTIVE,
        SEASON_API_STATUS.SCHEDULED,
        SEASON_API_STATUS.LOCKED,
      ];
    case SEASONS_FILTER_ID.SCHEDULED:
      return [SEASON_API_STATUS.SCHEDULED, SEASON_API_STATUS.LOCKED];
    case SEASONS_FILTER_ID.DRAFTS:
      return [SEASON_API_STATUS.DRAFT];
    case SEASONS_FILTER_ID.COMPLETED:
      return [SEASON_API_STATUS.COMPLETED];
    case SEASONS_FILTER_ID.CANCELED:
      return [SEASON_API_STATUS.CANCELLED];
    case SEASONS_FILTER_ID.ALL:
      return undefined;
    default: {
      const _exhaustive: never = filterId;
      return undefined;
    }
  }
};

/** Map a BE status string into the lifecycle bucket the listing page
 *  groups by. `grace` is bucketed alongside `active` because the BE
 *  uses it for the post-end settlement window — the season is still
 *  the "current" one from an admin's perspective. `locked` is
 *  bucketed with `scheduled` because it's the "Up Next" slot — a
 *  scheduled season that's been frozen so it can't be edited. */
export const seasonStatusToBucket = (
  status: SeasonApiStatus,
): SeasonLifecycleBucket => {
  switch (status) {
    case SEASON_API_STATUS.ACTIVE:
    case SEASON_API_STATUS.GRACE:
      return SEASON_LIFECYCLE_BUCKET.ACTIVE;
    case SEASON_API_STATUS.SCHEDULED:
    case SEASON_API_STATUS.LOCKED:
      return SEASON_LIFECYCLE_BUCKET.SCHEDULED;
    case SEASON_API_STATUS.DRAFT:
      return SEASON_LIFECYCLE_BUCKET.DRAFTS;
    case SEASON_API_STATUS.COMPLETED:
      return SEASON_LIFECYCLE_BUCKET.COMPLETED;
    case SEASON_API_STATUS.CANCELLED:
      return SEASON_LIFECYCLE_BUCKET.CANCELED;
    default: {
      // Exhaustiveness guard — if BE ever extends the enum, TS surfaces
      // the new value here so we can decide which bucket it falls into.
      const _exhaustive: never = status;
      return SEASON_LIFECYCLE_BUCKET.DRAFTS;
    }
  }
};

/** Stable fallback used when BE returns a record with neither `id`
 *  nor `season_id`. We never want `""` to bubble up because:
 *    1. It collides with every other id-less record in the same
 *       response and corrupts the React key on the list (silent
 *       reorder / unmount bugs).
 *    2. It produces a `:seasonId=` URL fragment that takes the user
 *       to a broken Season Details page on click.
 *  Instead we derive a deterministic placeholder from the record's
 *  natural-key fields so each id-less record gets its own key, and we
 *  emit a `console.warn` once per record so the malformed payload is
 *  visible in dev / observability tooling. The resulting id still
 *  fails to resolve a real season on navigation — that's the point:
 *  the failure is loud (404 / not-found state) instead of silently
 *  routing to the wrong record. */
const MISSING_SEASON_ID_PREFIX = "missing-season-id";

const buildMissingSeasonIdFallback = (record: SeasonApiRecord): string => {
  // `name + start_date + end_date` is good enough to keep two
  // distinct id-less records from colliding in practice. When BE
  // omits those too the fallback collapses to the prefix alone,
  // which is fine — at that point the record is effectively empty
  // and a key collision is the least of the problems.
  const parts = [record.name, record.start_date, record.end_date]
    .filter((part): part is string => Boolean(part))
    .join("|");
  return parts
    ? `${MISSING_SEASON_ID_PREFIX}:${parts}`
    : MISSING_SEASON_ID_PREFIX;
};

/** Resolve the season id from the API record. Accepts both `id` and
 *  `season_id` so the consumer keeps working through a BE rename.
 *  Falls back to a stable, observable placeholder (with a one-shot
 *  warning) when BE returns neither — never returns an empty string,
 *  which would corrupt the React key on the list and produce a
 *  broken `:seasonId=` URL on navigation. */
const resolveSeasonId = (record: SeasonApiRecord): string => {
  const id = record.id ?? record.season_id;
  if (id) return id;
  // eslint-disable-next-line no-console -- intentional dev signal for a
  // malformed BE payload; cheap to silence in production via the global
  // logger config if it ever gets noisy.
  console.warn(
    "[seasonsMappers] Season record is missing both `id` and `season_id`; falling back to a placeholder id.",
    { name: record.name, status: record.status },
  );
  return buildMissingSeasonIdFallback(record);
};

/** Format a [start, end] ISO pair into the date-range display string
 *  the card expects (e.g. `"May 1 - May 31, 2026"`). Falls back to
 *  whatever the BE sent if the dates aren't parseable so the card
 *  never renders an empty range — admins still see the raw input
 *  and can flag the malformed record. */
const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
/** BE season boundaries can arrive as date-only strings or UTC timestamps.
 *  Date-only strings have no timezone to convert, but timestamped boundaries
 *  must use the admin's local wall-clock so cards match the details page. */
export const parseSeasonBoundaryDate = (value: string) => {
  const trimmed = value?.trim() ?? "";
  if (DATE_ONLY_PATTERN.test(trimmed)) {
    return dayjs(trimmed, "YYYY-MM-DD", true);
  }

  const zoned = dayjs.utc(trimmed).local();
  return zoned.isValid() ? zoned : dayjs(trimmed);
};

const formatSeasonDateRange = (start: string, end: string): string => {
  const startDay = parseSeasonBoundaryDate(start);
  const endDay = parseSeasonBoundaryDate(end);
  if (!startDay.isValid() || !endDay.isValid()) {
    return `${start ?? ""} - ${end ?? ""}`;
  }
  // Drop the year on the start when both endpoints share the same
  // calendar year to keep the card tight ("May 1 - May 31, 2026"
  // rather than "May 1, 2026 - May 31, 2026"). When they differ
  // (cross-year seasons) both years stay so the range reads
  // unambiguously.
  const sameYear = startDay.year() === endDay.year();
  const startFormat = sameYear ? "MMM D" : "MMM D, YYYY";
  return `${startDay.format(startFormat)} - ${endDay.format("MMM D, YYYY")}`;
};

/** Compute the countdown phrase shown above the metrics on the card.
 *  Pure status-driven: active → "Ends in <N days>", scheduled /
 *  locked → "Starts in <N days>", drafts have no countdown (still
 *  works-in-progress), completed / canceled have no countdown
 *  (already in the past). When the season is mid-bucket but the
 *  reference timestamp has elapsed, returns `undefined` so the card
 *  collapses the row cleanly. */
const computeSeasonCountdown = (
  status: SeasonApiStatus,
  start: string,
  end: string,
): SeasonCountdown | undefined => {
  if (
    status === SEASON_API_STATUS.DRAFT ||
    status === SEASON_API_STATUS.COMPLETED ||
    status === SEASON_API_STATUS.CANCELLED
  ) {
    return undefined;
  }
  const now = dayjs.utc();
  const isActiveLike =
    status === SEASON_API_STATUS.ACTIVE || status === SEASON_API_STATUS.GRACE;
  const target = isActiveLike ? dayjs.utc(end) : dayjs.utc(start);
  if (!target.isValid()) return undefined;
  const diffDays = target.startOf("day").diff(now.startOf("day"), "day");
  if (diffDays <= 0) return undefined;
  const dayLabel = diffDays === 1 ? "day" : "days";
  const prefix = isActiveLike
    ? AppStrings.SEASON_COUNTDOWN_ENDS_IN
    : AppStrings.SEASON_COUNTDOWN_STARTS_IN;
  return { prefix, value: `${diffDays} ${dayLabel}` };
};

/** Resolve the top-right status pill for the card. `locked` mirrors
 *  the "Up Next" slot in the Figma — visually the same as a scheduled
 *  season but with the violet upNext tone. Active / grace use the
 *  green dot; draft uses amber; completed uses muted grey; cancelled
 *  uses its own `Canceled` label paired with the `canceled` tone (the
 *  tone currently aliases the completed muted-grey treatment in
 *  `SeasonCard.module.css` until design ships a dedicated style —
 *  swapping the style is a CSS-only change because the tone is
 *  already distinct from `completed`). */
const buildSeasonStatusBadge = (
  status: SeasonApiStatus,
): SeasonStatus | undefined => {
  switch (status) {
    case SEASON_API_STATUS.ACTIVE:
    case SEASON_API_STATUS.GRACE:
      return { label: AppStrings.SEASON_STATUS_ACTIVE, tone: "active" };
    case SEASON_API_STATUS.LOCKED:
      return { label: AppStrings.SEASON_STATUS_UP_NEXT, tone: "upNext" };
    case SEASON_API_STATUS.SCHEDULED:
      return undefined;
    case SEASON_API_STATUS.DRAFT:
      return { label: AppStrings.SEASON_STATUS_DRAFT, tone: "draft" };
    case SEASON_API_STATUS.COMPLETED:
      return { label: AppStrings.SEASON_STATUS_COMPLETED, tone: "completed" };
    case SEASON_API_STATUS.CANCELLED:
      return { label: AppStrings.SEASON_STATUS_CANCELED, tone: "canceled" };
    default: {
      const _exhaustive: never = status;
      return undefined;
    }
  }
};

/** Resolve the bottom-row affordance kind for the card. Mirrors the
 *  matrix the mock data already encoded: active / grace / locked
 *  seasons render a "Locked" pill; scheduled / draft seasons render
 *  the pencil-edit button; completed / cancelled seasons render the
 *  (currently disabled) "See Reports" link. */
const resolveSeasonTrailing = (
  status: SeasonApiStatus,
): SeasonTrailingKind => {
  switch (status) {
    case SEASON_API_STATUS.ACTIVE:
    case SEASON_API_STATUS.GRACE:
    case SEASON_API_STATUS.LOCKED:
      return "locked";
    case SEASON_API_STATUS.SCHEDULED:
    case SEASON_API_STATUS.DRAFT:
      return "edit";
    case SEASON_API_STATUS.COMPLETED:
    case SEASON_API_STATUS.CANCELLED:
      return "seeReports";
    default: {
      const _exhaustive: never = status;
      return "none";
    }
  }
};

/** Build the metrics row for the card. Only renders non-undefined
 *  counts — when BE omits one of the counts the card silently drops
 *  that pill rather than rendering "0 Challenges". BE field names
 *  are singular (`challenge_count`, `task_count`) per the live
 *  payload; double-check the type before renaming here. */
const buildSeasonMetrics = (
  record: SeasonApiRecord,
): ReadonlyArray<SeasonMetric> => {
  const metrics: SeasonMetric[] = [];
  if (typeof record.challenge_count === "number") {
    metrics.push({
      id: "challenges",
      label: AppStrings.SEASON_METRIC_CHALLENGES,
      value: record.challenge_count,
    });
  }
  if (typeof record.task_count === "number") {
    metrics.push({
      id: "tasks",
      label: AppStrings.SEASON_METRIC_TASKS,
      value: record.task_count,
    });
  }
  return metrics;
};

/** Resolve the title the Game Pass admin UI should display/edit.
 *
 * The season edit flow persists `name`, not `display_name`, so the UI
 * must prefer `name` or a successful rename can look like a no-op when
 * BE keeps returning a stale `display_name`. We still fall back to a
 * non-empty `display_name` as a defensive backup for malformed payloads
 * where `name` is unexpectedly blank. */
const resolveSeasonName = (record: SeasonApiRecord): string => {
  if (typeof record.name === "string" && record.name.trim().length > 0) {
    return record.name;
  }
  const override = record.display_name;
  if (typeof override === "string" && override.trim().length > 0) {
    return override;
  }
  return record.name;
};

/** Translate a single API record into the UI model the existing
 *  `SeasonCard` / `SeasonsList` consume. Exported for tests so the
 *  contract stays auditable without mounting the page. */
export const mapApiSeasonToSeasonData = (
  record: SeasonApiRecord,
): SeasonData => ({
  id: resolveSeasonId(record),
  name: resolveSeasonName(record),
  dateRange: formatSeasonDateRange(record.start_date, record.end_date),
  countdown: computeSeasonCountdown(
    record.status,
    record.start_date,
    record.end_date,
  ),
  metrics: buildSeasonMetrics(record),
  status: buildSeasonStatusBadge(record.status),
  trailing: resolveSeasonTrailing(record.status),
});

/** Compare two records so the most-recently-*created* one sorts first
 *  (descending `created_at`). Records missing `created_at` — or with an
 *  unparseable value — are treated as the oldest possible so they sink
 *  to the bottom rather than jumping to the top on a malformed payload.
 *  Equal timestamps return 0, and because `Array.prototype.sort` is
 *  stable (ES2019+) the original BE order is preserved on ties, so the
 *  sort never introduces flicker between records the BE considers
 *  equivalent. */
const compareByCreatedAtDesc = (
  a: SeasonApiRecord,
  b: SeasonApiRecord,
): number => {
  const toMs = (value?: string): number => {
    if (!value) return -Infinity;
    const ms = dayjs.utc(value).valueOf();
    return Number.isFinite(ms) ? ms : -Infinity;
  };
  return toMs(b.created_at) - toMs(a.created_at);
};

/** Drop every API record into the lifecycle bucket the listing page
 *  groups by. Records are first sorted by `created_at` descending so the
 *  most recently created season surfaces at the top of every bucket
 *  (product requirement: "recent on top"). The Active bucket only keeps
 *  the most-recent record (latest `start_date`) because the UI surfaces
 *  a single Active Season card; the other buckets are accumulated arrays
 *  that inherit the created-at ordering. */
export const partitionSeasonsByStatus = (
  records: ReadonlyArray<SeasonApiRecord>,
): PartitionedSeasons => {
  // Copy before sorting — `records` is a ReadonlyArray and may be the
  // React Query cache, which must not be mutated in place.
  const sortedRecords = [...records].sort(compareByCreatedAtDesc);
  const initial: PartitionedSeasons = {
    active: null,
    scheduled: [],
    drafts: [],
    completed: [],
    canceled: [],
  };
  // Track the API-side start_date for the current active winner so we
  // can compare without re-parsing on every record. Only relevant when
  // multiple active / grace records arrive (defensive — the BE
  // contract today emits one at most).
  let activeStartMs = -Infinity;

  sortedRecords.forEach((record) => {
    const ui = mapApiSeasonToSeasonData(record);
    const bucket = seasonStatusToBucket(record.status);
    switch (bucket) {
      case SEASON_LIFECYCLE_BUCKET.ACTIVE: {
        const startMs = dayjs.utc(record.start_date).valueOf();
        if (initial.active == null || startMs > activeStartMs) {
          initial.active = ui;
          activeStartMs = Number.isFinite(startMs) ? startMs : activeStartMs;
        }
        break;
      }
      case SEASON_LIFECYCLE_BUCKET.SCHEDULED:
        initial.scheduled.push(ui);
        break;
      case SEASON_LIFECYCLE_BUCKET.DRAFTS:
        initial.drafts.push(ui);
        break;
      case SEASON_LIFECYCLE_BUCKET.COMPLETED:
        initial.completed.push(ui);
        break;
      case SEASON_LIFECYCLE_BUCKET.CANCELED:
        initial.canceled.push(ui);
        break;
      default: {
        const _exhaustive: never = bucket;
      }
    }
  });
  return initial;
};
